import { Wrench, MessageCircle, User, Sun, Zap } from "lucide-react";
import { useFeature } from "@/lib/features";
import { openExternal } from "@/lib/openExternal";

export type TabId = "tasks" | "tools" | "chat" | "today" | "mykin";

interface BottomNavBarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  /** When true (e.g. mobile keyboard is open), slide the bar off-screen so
   *  it can't overlap the input bar or sit underneath the keyboard. */
  hidden?: boolean;
}

const BottomNavBar = ({ activeTab, onTabChange, hidden = false }: BottomNavBarProps) => {
  const tasksTabEnabled = useFeature("tasksTab");
  const tabs: { id: TabId; label: string; icon: typeof Wrench }[] = [
    ...(tasksTabEnabled ? [{ id: "tasks" as TabId, label: "Tasks", icon: Zap }] : []),
    { id: "today", label: "Today", icon: Sun },
    { id: "chat", label: "Chat", icon: MessageCircle },
    { id: "tools", label: "Tools", icon: Wrench },
    { id: "mykin", label: "My Kin", icon: User },
  ];

  return (
  <nav
    aria-hidden={hidden}
    className="fixed left-0 right-0 z-50"
    style={{
      bottom: 0,
      backgroundColor: "#F0F0FF",
      borderTop: "1px solid #E8E6F4",
      paddingTop: "8px",
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
      transform: hidden ? "translateY(calc(100% + env(safe-area-inset-bottom, 0px)))" : "translateY(0)",
      transition: "transform 200ms ease",
      pointerEvents: hidden ? "none" : "auto",
      willChange: "transform",
    }}
  >
    <div className="flex items-center justify-around max-w-2xl mx-auto w-full px-4">
      {tabs.map((tab) => {
        const active = activeTab === tab.id;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className="flex flex-col items-center gap-0.5 py-1 px-3 transition-colors flex-1"
            style={{ color: active ? "#5050C8" : "#54525C" }}
          >
            <Icon size={20} />
            <span className="font-body text-[10px] font-semibold">{tab.label}</span>
            {active && (
              <span
                className="block rounded-full"
                style={{ width: 4, height: 4, backgroundColor: "#5050C8", marginTop: 2 }}
              />
            )}
          </button>
        );
      })}
    </div>
    <p
      className="font-body text-center px-4 pt-1.5 pb-1"
      style={{
        fontSize: 10,
        color: "#54525C",
        lineHeight: 1.3,
        // Shift JUST the footer text down into the safe-area-inset-bottom zone
        // (capped at 18px) so it sits ~16px above the home indicator instead
        // of leaving the inset as empty padding. Tab icons and outer nav
        // padding are unchanged.
        transform:
          "translateY(min(max(env(safe-area-inset-bottom, 0px) - 18px, 0px), 18px))",
      }}
    >
      Powered by AI · Built with{" "}
      <span aria-hidden="true">💜</span> by{" "}
      <span style={{ color: "#5050C8", fontWeight: 600 }}>Kinyoudoitall</span>
      {" · "}
      <a
        href="https://kinyoudoitall.com/privacy"
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => { e.preventDefault(); openExternal("https://kinyoudoitall.com/privacy"); }}
        style={{ color: "#5050C8", textDecoration: "underline" }}
      >
        Privacy Policy
      </a>
    </p>
  </nav>
  );
};

export default BottomNavBar;
